/* =====================================================================
 * CNM Sticky Bottom ATC
 * Vanilla JS, no dependencies.
 *
 * Responsibilities:
 *   • Mirror hero state (variant title + price + sub indicator) by
 *     listening to cnm:variant:change events from the hero
 *   • On mobile (<750px): always visible
 *   • On desktop: fade in once user scrolls past the hero
 *   • On click: post to /cart/add.js with current variant + selling_plan
 *     then trigger Dawn's cart drawer to open
 * ===================================================================== */

(function () {
  'use strict';

  const MOBILE_BP = 750;

  function isMobile() {
    return window.matchMedia(`(max-width: ${MOBILE_BP - 1}px)`).matches;
  }

  function formatPrice(pennies) {
    return '£' + (pennies / 100).toFixed(2);
  }

  function initSticky(root) {
    const hero            = document.querySelector(root.dataset.heroSelector || '[data-cnm-hero]');
    const variantEl       = root.querySelector('[data-cnm-sticky-variant]');
    const lastsEl         = root.querySelector('[data-cnm-sticky-lasts]');
    const subSepEl        = root.querySelector('[data-cnm-sticky-sub-sep]');
    const subPillEl       = root.querySelector('[data-cnm-sticky-sub-pill]');
    const priceEl         = root.querySelector('[data-cnm-sticky-price]');
    const ctaBtn          = root.querySelector('[data-cnm-sticky-cta]');

    let currentVariantId  = null;
    let currentSellingPlan = null;

    // ─── State sync from hero ─────────────────────────────────────

    function readSelectedCardLabel() {
      const selectedCard = hero?.querySelector('.cnm-bundle__card.is-selected');
      const titleEl = selectedCard?.querySelector('.cnm-bundle__card-title');
      const lastsEl = selectedCard?.querySelector('.cnm-bundle__card-eyebrow');
      return {
        title: titleEl ? titleEl.textContent.trim() : '',
        lasts: lastsEl ? lastsEl.textContent.trim() : '',
      };
    }

    function syncFromEvent(detail) {
      currentVariantId = detail.variantId;
      currentSellingPlan = detail.isSubscribed ? (hero?.dataset.sellingPlanId || null) : null;

      if (priceEl) priceEl.textContent = formatPrice(detail.price);

      const labels = readSelectedCardLabel();
      if (variantEl && labels.title) {
        variantEl.textContent = ' · ' + labels.title;
      }
      if (lastsEl && labels.lasts) {
        lastsEl.textContent = labels.lasts.charAt(0).toUpperCase() + labels.lasts.slice(1).toLowerCase() + ' of protection';
      }

      // Sub pill visibility
      if (subPillEl) subPillEl.hidden = !detail.isSubscribed;
      if (subSepEl)  subSepEl.hidden  = !detail.isSubscribed;
    }

    // Bridge to hero's custom event
    if (hero) {
      hero.addEventListener('cnm:variant:change', (e) => syncFromEvent(e.detail));

      // Initial sync — read hero's pre-rendered default state
      const initialCard = hero.querySelector('.cnm-bundle__card.is-selected');
      if (initialCard) {
        const initialPrice = parseInt(initialCard.dataset.cnmPrice || '0', 10);
        const heroSubCheckbox = hero.querySelector('[data-cnm-sub-checkbox]');
        const initialSub = heroSubCheckbox ? heroSubCheckbox.checked : false;
        const visualPrice = initialSub ? Math.round(initialPrice * 0.85) : initialPrice;
        syncFromEvent({
          variantId: initialCard.dataset.cnmVariantId,
          price: visualPrice,
          rawPrice: initialPrice,
          isSubscribed: initialSub,
        });
      }
    }

    // ─── Visibility ───────────────────────────────────────────────

    function show() { root.classList.add('is-visible'); root.setAttribute('aria-hidden', 'false'); }
    function hide() { root.classList.remove('is-visible'); root.setAttribute('aria-hidden', 'true'); }

    if (isMobile()) {
      show();
    } else if (hero && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          // Hero in view → hide sticky. Hero scrolled past → show sticky.
          if (entry.intersectionRatio < 0.05) {
            show();
          } else {
            hide();
          }
        });
      }, { threshold: [0, 0.05, 0.5] });
      observer.observe(hero);
    } else {
      show(); // fallback if no IO or no hero
    }

    // Re-evaluate on resize across breakpoint
    window.addEventListener('resize', () => {
      if (isMobile()) show();
    });

    // ─── Add to cart action ───────────────────────────────────────

    if (ctaBtn) {
      ctaBtn.addEventListener('click', async () => {
        if (!currentVariantId) return;

        ctaBtn.disabled = true;
        const originalLabel = ctaBtn.querySelector('.cnm-sticky__cta-label')?.textContent;
        const labelEl = ctaBtn.querySelector('.cnm-sticky__cta-label');
        if (labelEl) labelEl.textContent = 'Adding…';

        const formData = new FormData();
        formData.append('id', currentVariantId);
        formData.append('quantity', '1');
        if (currentSellingPlan) formData.append('selling_plan', currentSellingPlan);

        try {
          const res = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            body: formData,
            headers: { 'Accept': 'application/javascript' },
          });

          if (!res.ok) throw new Error('Cart add failed: ' + res.status);

          // Refresh Dawn's cart drawer / notification
          // Dawn listens to PUB_SUB_EVENTS.cartUpdate — dispatch via window.PUB_SUB_EVENTS if available
          if (window.publish && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cnm-sticky-atc' });
          }

          // Trigger cart drawer open (Dawn convention)
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open(ctaBtn);
          } else {
            // Fallback: navigate to cart
            const cartIconBubble = document.getElementById('cart-icon-bubble');
            if (cartIconBubble) cartIconBubble.click();
          }

          if (labelEl && originalLabel) labelEl.textContent = 'Added ✓';
          setTimeout(() => {
            if (labelEl && originalLabel) labelEl.textContent = originalLabel;
            ctaBtn.disabled = false;
          }, 1200);
        } catch (err) {
          console.error('[CNM Sticky ATC] Add to cart error:', err);
          if (labelEl && originalLabel) labelEl.textContent = 'Error — try again';
          ctaBtn.disabled = false;
        }
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-cnm-sticky]').forEach(initSticky);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target.querySelector('[data-cnm-sticky]');
    if (root) initSticky(root);
  });
})();
